// WebGPU renderer, scene, shaders, and animation loop
import * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, uint, instanceIndex, uniform, uniformArray, storage,
  If, Loop, length, clamp, mix, sin, cos, floor, abs, min, max, exp, dot, normalize, smoothstep, positionLocal
} from 'three/tsl';

import type { AppParams, Wave } from './types';
import { COLORS } from './data';
import { PRESETS, hexToRgb } from './presets';
import { buildShapes, loadShape, MAXF } from './shapes';
import { classifyChord, updateHarmonicHud, noteName, intervalShapeFor } from './chords';
import { gestureState, runGestureTracking, startWebcam, stopWebcam, loadMediaPipe, resetGestureState } from './gestures';
import { toggleRecording, requestSnap, checkSnap } from './recording';
import { PianoController } from './piano';
import { SynthEngine } from './synth';

const MAXW = 24; // concurrent ripples

export interface RendererState {
  renderer: THREE.WebGPURenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: any;
  P: AppParams;
  G: number;
  CELLS: number;
  EXT: number;
  HALF: number;
  waves: Wave[];
  active: Set<number>;
  heldKeysLastPulse: Record<number, number>;
  synth: SynthEngine;
  // Uniforms
  uWaveA: THREE.UniformArrayNode;
  uWaveB: THREE.UniformArrayNode;
  uCount: { value: number };
  uTime: { value: number };
  uBlend: { value: number };
  uMode: { value: number };
  uWidth: { value: number };
  uDecay: { value: number };
  uAmp: { value: number };
  uHeight: { value: number };
  uSpin: { value: number };
  uSpacing: { value: number };
  uHandPos: { value: THREE.Vector3 };
  uHandOpen: { value: number };
  uHeadTilt: { value: THREE.Vector2 };
  uThumbExt: { value: number };
  uIndexExt: { value: number };
  uMiddleExt: { value: number };
  uRingExt: { value: number };
  uPinkyExt: { value: number };
  uHandRotY: { value: number };
  uMorph: { value: number };
  uFromPlanes: THREE.UniformArrayNode;
  uToPlanes: THREE.UniformArrayNode;
  uFromCount: { value: number };
  uToCount: { value: number };
  uFromSphere: { value: number };
  uToSphere: { value: number };
  uFromR: { value: number };
  uToR: { value: number };
  uRestTint: { value: THREE.Vector3 };
  uEmissiveBoost: { value: number };
  // Shape state
  SHAPES: Record<string, any>;
  curShapeKey: string;
  morphActive: boolean;
  morphT: number;
  fromArr: THREE.Vector4[];
  toArr: THREE.Vector4[];
  // Gesture visual
  gestureVisualSphere: THREE.Mesh | null;
  pc: THREE.PointLight;
  piano: PianoController;
}

export function showError(title: string, detail: string) {
  const loader = document.getElementById('loadingOverlay');
  if (loader) loader.style.display = 'none';
  const b = document.getElementById('err'), m = document.getElementById('errmsg');
  if (!b || !m) return;
  m.innerHTML = (title ? ('<b style="color:#ffb0b0">' + title + '</b>\n\n') : '') + (detail || '');
  b.style.display = 'flex';
}

const boot = (m: string) => {
  const b = document.getElementById('boot');
  if (b) b.textContent = m;
};

function applyPreset(state: RendererState, name: string) {
  const preset = PRESETS[name] || PRESETS.Void;
  state.P.visualPreset = name;
  document.documentElement.style.setProperty('--accent', preset.accent);
  document.documentElement.style.setProperty('--aura', preset.aura);
  document.documentElement.style.setProperty('--glow', preset.glow);
  document.documentElement.style.setProperty('--void', '#' + preset.bg.toString(16).padStart(6, '0'));
  state.scene.background = new THREE.Color(preset.bg);
  state.scene.fog = new THREE.FogExp2(preset.fog, 0.020);
  state.renderer.toneMappingExposure = preset.exposure;
  state.uRestTint.value.set(...preset.rest);
  state.uEmissiveBoost.value = preset.emissive;
}

function syncUniforms(state: RendererState) {
  const P = state.P;
  state.uWidth.value = P.waveWidth;
  state.uDecay.value = P.waveDecay;
  state.uAmp.value = P.waveAmp;
  state.uHeight.value = P.heightMul;
  state.uMode.value = P.blendMode;
  state.uSpin.value = P.spin;
}

function spawnWave(state: RendererState, midi: number, isSustainPulse = false, velocity = 0.8) {
  const [, hex] = COLORS[midi] || [, "#ffffff"];
  const prog = (midi - 21) / 87;
  const ox = (prog - 0.5) * state.HALF * 1.4;
  const oz = ((midi % 12) / 11 - 0.5) * state.HALF * 1.0;
  if (state.waves.length >= MAXW) state.waves.shift();

  const speed = state.P.waveSpeed * (0.78 + velocity * 0.34);
  const color = hexToRgb(hex);
  if (isSustainPulse) {
    color.r *= 0.65; color.g *= 0.65; color.b *= 0.65;
  }
  state.waves.push({ ox, oz, age: 0, speed, color });
}

function setShape(state: RendererState, key: string) {
  if (!state.SHAPES[key]) key = '4';
  if (key === state.curShapeKey && state.uMorph.value >= 1) return;
  loadShape(
    state.fromArr, state.uFromCount, state.uFromSphere, state.uFromR,
    state.SHAPES[state.curShapeKey]
  );
  loadShape(
    state.toArr, state.uToCount, state.uToSphere, state.uToR,
    state.SHAPES[key]
  );
  state.uMorph.value = 0;
  state.morphActive = true;
  state.morphT = 0;
  state.curShapeKey = key;
}

function detect(state: RendererState) {
  const chordEl = document.getElementById('chord');
  const shapeEl = document.getElementById('shape');
  const sorted = [...state.active].sort((a, b) => a - b);
  const info = classifyChord(sorted);
  updateHarmonicHud(info);

  if (state.active.size < 2) {
    state.uBlend.value = 0.45;
    if (chordEl) chordEl.textContent = info.symbol;
    if (shapeEl) shapeEl.textContent = state.active.size === 1 ? `◆ Sphere · single pitch field` : '';
    if (state.P.shapeMode === 'Dynamic') setShape(state, 'sphere');
    return;
  }

  if (info.shape) {
    state.uBlend.value = info.blend;
    if (chordEl) chordEl.textContent = info.symbol;
    if (shapeEl) shapeEl.textContent = `◆ ${info.shape.d3} · ${info.shape.name} energy · blend ${Math.round(info.blend * 100)}%`;
    if (state.P.shapeMode === 'Dynamic') setShape(state, info.shape.shape);
  } else {
    state.uBlend.value = 0.5;
    if (chordEl) chordEl.textContent = info.symbol || 'Complex Chord';
    if (shapeEl) shapeEl.textContent = '◆ Polyhedron · complex harmonic cluster';
    if (state.P.shapeMode === 'Dynamic') setShape(state, '5');
  }
}

function noteOn(state: RendererState, midi: number, vel = 0.8) {
  if (midi < 21 || midi > 108 || state.active.has(midi)) return;
  const hint = document.getElementById('hint');
  if (hint) hint.style.display = 'none';
  state.active.add(midi);
  state.synth.play(midi, state.P.synthWave, { a: state.P.attack, d: 0.2, s: 0.6, r: state.P.release });
  spawnWave(state, midi, false, vel);
  state.heldKeysLastPulse[midi] = performance.now();
  detect(state);
  state.piano.paintKeys();
}

function noteOff(state: RendererState, midi: number) {
  if (!state.active.has(midi)) return;
  state.active.delete(midi);
  delete state.heldKeysLastPulse[midi];
  state.synth.stop(midi, state.P.release);
  detect(state);
  state.piano.paintKeys();
}

export async function initApp() {
  // Error handling
  addEventListener('error', e => showError('Script error',
    (e.message || '') + '\n' + (e.filename || '') + ':' + (e.lineno || '') + '\n\n' + ((e.error && e.error.stack) || '')));
  addEventListener('unhandledrejection', e => {
    const r = e.reason || {};
    showError('Unhandled rejection', (r.message || String(r)) + '\n\n' + (r.stack || ''));
  });

  if (!navigator.gpu) {
    showError('WebGPU not available', 'Open in Chrome / Edge 113+ or Safari Technology Preview.\nLinux Chrome may need <code>--enable-unsafe-webgpu</code>.');
    return;
  }

  // Suppress MediaPipe XNNPACK warnings
  const originalConsoleError = console.error;
  console.error = function (...args) {
    const msg = args.join(' ');
    if (msg.includes('XNNPACK') || msg.includes('TensorFlow Lite') || msg.includes('delegate') || msg.includes('Created TensorFlow Lite')) {
      console.log("[MediaPipe Diagnostic Info Redirect]", ...args);
      return;
    }
    originalConsoleError.apply(console, args);
  };

  const urlp = new URLSearchParams(location.search);
  const G = Math.max(16, Math.min(48, parseInt(urlp.get('grid')!) || 34));
  const CELLS = G * G;

  const P: AppParams = {
    waveSpeed: 22, waveDecay: 1.4, waveWidth: 8, waveAmp: 1.6, heightMul: 3.4,
    spacing: 0.95, blendMode: 0, shapeMode: 'Dynamic', synthWave: 'triangle',
    attack: 0.05, release: 1.3, spin: 0.35, sustainPulseRate: 0.40,
    gestureControl: 'Off', visualPreset: 'Void'
  };

  try {
    boot('creating renderer');

    // Renderer
    const renderer = new THREE.WebGPURenderer({
      canvas: document.getElementById('c') as HTMLCanvasElement,
      antialias: true
    });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.setSize(innerWidth, innerHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020205);
    scene.fog = new THREE.FogExp2(0x020205, 0.020);

    const EXT = G * P.spacing, HALF = EXT * 0.5;

    // Camera
    const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.1, 1000);
    camera.position.set(0, HALF * 1.15, HALF * 1.35);

    // Controls
    let controls: any = null;
    try {
      const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.maxPolarAngle = Math.PI / 2 - 0.04;
      controls.minDistance = HALF * 0.5;
      controls.maxDistance = HALF * 3;
      controls.target.set(0, HALF * 0.4, 0);
    } catch (e) {
      console.warn('OrbitControls', (e as Error).message);
      camera.lookAt(0, 0, 0);
    }

    // Lights
    scene.add(new THREE.HemisphereLight(0x8899cc, 0x08040a, 0.35));
    const d1 = new THREE.DirectionalLight(0xffffff, 1.6); d1.position.set(10, 30, 10);
    const d2 = new THREE.DirectionalLight(0xff2400, 0.9); d2.position.set(-15, 14, -15);
    const pc = new THREE.PointLight(0x00ffff, 120, HALF * 3, 2); pc.position.set(0, HALF * 0.4, 0);
    scene.add(d1, d2, pc);

    // GPU cell field
    const cellBuf = new THREE.StorageInstancedBufferAttribute(CELLS, 4);
    const cellStore = storage(cellBuf, 'vec4', CELLS);
    const cellRead = storage(cellBuf, 'vec4', CELLS).toReadOnly();

    // Wave uniforms
    const waveArrA = Array.from({ length: MAXW }, () => new THREE.Vector4());
    const waveArrB = Array.from({ length: MAXW }, () => new THREE.Vector4());
    const uWaveA = uniformArray(waveArrA);
    const uWaveB = uniformArray(waveArrB);
    const uCount = uniform(0);
    const uSpacing = uniform(P.spacing), uWidth = uniform(P.waveWidth), uDecay = uniform(P.waveDecay), uAmp = uniform(P.waveAmp);
    const uBlend = uniform(0.45), uMode = uniform(0), uTime = uniform(0), uHeight = uniform(P.heightMul), uSpin = uniform(P.spin);

    // Gesture uniforms
    const uHandPos = uniform(new THREE.Vector3(0, -999, 0));
    const uHandOpen = uniform(1.0);
    const uHandRotY = uniform(0.0);
    const uHeadTilt = uniform(new THREE.Vector2(0, 0));
    const uThumbExt = uniform(1.0);
    const uIndexExt = uniform(1.0);
    const uMiddleExt = uniform(1.0);
    const uRingExt = uniform(1.0);
    const uPinkyExt = uniform(1.0);

    // Preset uniforms
    const uRestTint = uniform(new THREE.Vector3(...PRESETS.Void.rest));
    const uEmissiveBoost = uniform(PRESETS.Void.emissive);

    // Wave-field compute shader
    boot('compiling compute');
    const fi = float(instanceIndex);
    const cz = floor(fi.div(float(G)));
    const cx = fi.sub(cz.mul(float(G)));
    const gx = cx.sub(float(G * 0.5)).mul(uSpacing);
    const gz = cz.sub(float(G * 0.5)).mul(uSpacing);

    const fieldFn = Fn(() => {
      const height = float(0).toVar();
      const primI = float(0).toVar(), secI = float(0).toVar();
      const primC = vec3(0).toVar(), secC = vec3(0).toVar();

      Loop({ start: uint(0), end: uint(MAXW), type: 'uint', condition: '<' }, ({ i }) => {
        If(float(i).lessThan(uCount), () => {
          const a = uWaveA.element(i);
          const b = uWaveB.element(i);
          const dx = gx.sub(a.x), dz = gz.sub(a.y);
          const dist = length(vec3(dx, float(0), dz));
          const radius = a.z.mul(a.w);
          const df = abs(dist.sub(radius));
          If(df.lessThan(uWidth.mul(1.5)), () => {
            const q = df.div(uWidth.mul(0.5));
            const inten = exp(q.mul(q).negate()).mul(exp(uDecay.mul(a.z).negate())).mul(uAmp);
            If(inten.greaterThan(0.01), () => {
              height.addAssign(inten);
              If(inten.greaterThan(primI), () => {
                secI.assign(primI); secC.assign(primC); primI.assign(inten); primC.assign(b.xyz);
              }).ElseIf(inten.greaterThan(secI), () => {
                secI.assign(inten); secC.assign(b.xyz);
              });
            });
          });
        });
      });

      const col = vec3(0).toVar();
      If(primI.greaterThan(0.0), () => {
        If(secI.greaterThan(0.0), () => {
          If(uMode.lessThan(0.5), () => {
            col.assign(mix(primC, secC, uBlend).mul(float(1.0).add(height.mul(1.4))));
          }).Else(() => {
            col.assign(primC.add(secC).mul(float(1.0).add(height.mul(0.9))));
          });
        }).Else(() => {
          col.assign(primC.mul(float(1.0).add(height.mul(1.2))));
        });
      });
      cellStore.element(instanceIndex).assign(vec4(col, height));
    })().compute(CELLS);

    // Shapes
    const SHAPES = buildShapes();
    const fromArr = Array.from({ length: MAXF }, () => new THREE.Vector4());
    const toArr = Array.from({ length: MAXF }, () => new THREE.Vector4());
    const uFromPlanes = uniformArray(fromArr);
    const uToPlanes = uniformArray(toArr);
    const uFromCount = uniform(0), uToCount = uniform(0);
    const uFromSphere = uniform(0), uToSphere = uniform(0);
    const uFromR = uniform(0.42), uToR = uniform(0.42);
    const uMorph = uniform(1);

    // Start on cube
    loadShape(fromArr, uFromCount, uFromSphere, uFromR, SHAPES['4']);
    loadShape(toArr, uToCount, uToSphere, uToR, SHAPES['4']);

    // Shape material with TSL position node
    const nx = gx.div(HALF).mul(0.5).add(0.5);
    const nz = gz.div(HALF).mul(0.5).add(0.5);
    const cellN = cellRead.element(instanceIndex);
    const hN = cellN.w;
    const restCol = vec3(
      nx.mul(0.35).add(uRestTint.x),
      nx.oneMinus().mul(0.16).add(nz.mul(0.14)).add(uRestTint.y),
      nz.mul(0.42).add(uRestTint.z)
    ).mul(0.18);

    const shapeMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.66, roughness: 0.28, flatShading: true });

    shapeMat.positionNode = Fn(() => {
      const dir = normalize(positionLocal);
      const rF = radiusFor(dir, uFromPlanes, uFromCount, uFromSphere, uFromR);
      const rT = radiusFor(dir, uToPlanes, uToCount, uToSphere, uToR);
      const r = mix(rF, rT, smoothstep(0.0, 1.0, uMorph));

      const instPos = vec3(gx, float(0), gz);
      const toHand = uHandPos.sub(instPos);
      const distToHand = length(toHand);
      // Extremely tight influence - only cells right under the hand are affected
      const handInfluence = smoothstep(float(HALF * 0.12), float(0.0), distToHand);
      // Nearly imperceptible scale change
      const gestureScale = mix(float(0.99), float(1.01), uHandOpen);
      const handScale = mix(float(1.0), gestureScale, handInfluence);
      const sp = dir.mul(r).mul(handScale);

      const safeDir = normalize(toHand.add(vec3(0.0001, 0.0001, 0.0001)));
      const thumbPolarity = mix(float(1.0), float(-1.0), uThumbExt);
      // Barely noticeable warp
      const warpOffset = safeDir.mul(thumbPolarity).mul(handInfluence.mul(handInfluence)).mul(float(0.02));

      const pinkySpike = uPinkyExt.mul(float(0.015)).mul(handInfluence);
      const displacedSp = sp.add(dir.mul(pinkySpike));

      const indexStretch = uIndexExt.mul(float(0.03)).mul(handInfluence);
      const ly = displacedSp.y.mul(float(1.0).add(hN.mul(uHeight)).add(indexStretch)).add(warpOffset.y);

      const sq = float(1.0).sub(min(float(0.35), hN.mul(0.22)));
      const lx = displacedSp.x.mul(sq).add(warpOffset.x);
      const lz = displacedSp.z.mul(sq).add(warpOffset.z);

      const swirlPhase = length(vec3(gx.sub(uHandPos.x), float(0), gz.sub(uHandPos.z))).mul(float(0.55)).sub(uTime.mul(float(3.6)));
      const swirlDrive = mix(float(0.20), float(0.22), uMiddleExt).mul(handInfluence);
      const ang = sin(swirlPhase).mul(hN).mul(uSpin).mul(float(1.0).add(swirlDrive.mul(float(0.2))));
      const ca = cos(ang), sa = sin(ang);
      const rx = lx.mul(ca).sub(lz.mul(sa));
      const rz = lx.mul(sa).add(lz.mul(ca));

      const headSkewX = uHeadTilt.x.mul(ly).mul(0.35);
      const headSkewZ = uHeadTilt.y.mul(ly).mul(0.35);

      // Global Y-axis rotation driven by hand X position
      const finalX = gx.add(rx).add(headSkewX);
      const finalZ = gz.add(rz).add(headSkewZ);
      const rotCa = cos(uHandRotY);
      const rotSa = sin(uHandRotY);
      const rotatedX = finalX.mul(rotCa).sub(finalZ.mul(rotSa));
      const rotatedZ = finalX.mul(rotSa).add(finalZ.mul(rotCa));

      return vec3(rotatedX, ly.add(hN), rotatedZ);
    })();

    shapeMat.colorNode = cellN.xyz.add(restCol.mul(clamp(float(1.0).sub(hN.mul(2.0)), 0.0, 1.0)));
    shapeMat.emissiveNode = cellN.xyz.mul(clamp(hN, 0.0, 2.0)).mul(uEmissiveBoost).add(vec3(0.0, 0.9, 1.0).mul(uRingExt).mul(0.05));

    // Build instanced mesh
    boot('building grid');
    const mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 2), shapeMat, CELLS);
    mesh.frustumCulled = false;
    scene.add(mesh);

    // Gesture visual sphere
    const geo = new THREE.SphereGeometry(HALF * 0.08, 16, 16);
    const mat = new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0.8 });
    const gestureVisualSphere = new THREE.Mesh(geo, mat);
    gestureVisualSphere.visible = false;
    scene.add(gestureVisualSphere);

    // State
    const synth = new SynthEngine();
    const waves: Wave[] = [];
    const active = new Set<number>();
    const heldKeysLastPulse: Record<number, number> = {};

    const state: RendererState = {
      renderer, scene, camera, controls, P, G, CELLS, EXT, HALF,
      waves, active, heldKeysLastPulse, synth,
      uWaveA, uWaveB, uCount, uTime, uBlend, uMode, uWidth, uDecay, uAmp, uHeight, uSpin, uSpacing,
      uHandPos, uHandOpen, uHeadTilt, uThumbExt, uIndexExt, uMiddleExt, uRingExt, uPinkyExt,
      uHandRotY,
      uMorph, uFromPlanes, uToPlanes, uFromCount, uToCount, uFromSphere, uToSphere, uFromR, uToR,
      uRestTint, uEmissiveBoost,
      SHAPES, curShapeKey: '4', morphActive: false, morphT: 0,
      fromArr, toArr,
      gestureVisualSphere, pc,
      piano: null! // set below
    };

    // Piano
    const piano = new PianoController(active,
      (midi, vel) => noteOn(state, midi, vel),
      (midi) => noteOff(state, midi)
    );
    state.piano = piano;

    // Build panel controls
    buildPanel(state);

    // Recording buttons
    document.getElementById('btnRec')?.addEventListener('click', () => toggleRecording(synth));
    document.getElementById('btnSnap')?.addEventListener('click', () => requestSnap());

    // Resize
    addEventListener('resize', () => {
      camera.aspect = innerWidth / innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(innerWidth, innerHeight);
    });

    // PWA install button
    setupPWAInstall();

    // Animation loop
    const MORPH_DUR = 0.45;
    let last = performance.now(), start = last, frames = 0, fpsT = 0;

    function animate() {
      const now = performance.now();
      let dt = (now - last) / 1000;
      last = now;
      if (dt > 0.05) dt = 0.05;
      const t = (now - start) / 1000;
      uTime.value = t;

      // Shape morph
      if (state.morphActive) {
        state.morphT += dt;
        uMorph.value = Math.min(1, state.morphT / MORPH_DUR);
        if (uMorph.value >= 1) state.morphActive = false;
      }

      // Sustain pulses
      for (const midi in state.heldKeysLastPulse) {
        const lastPulseTime = state.heldKeysLastPulse[midi];
        if (now - lastPulseTime > state.P.sustainPulseRate * 1000) {
          spawnWave(state, parseInt(midi), true, 0.45);
          state.heldKeysLastPulse[midi] = now;
        }
      }

      // Gesture tracking
      runGestureTracking(now, state.P.gestureControl, state.EXT, (x, z, color) => {
        if (state.waves.length >= MAXW) state.waves.shift();
        state.waves.push({ ox: x, oz: z, age: 0, speed: state.P.waveSpeed * 0.9, color });
      });

      // Update gesture uniforms
      uHandPos.value.set(state.gestureVisualSphere ? gestureState.handPos.x : 0, 0.4, gestureState.handPos.z);
      uHandOpen.value = gestureState.handOpen;
      uHeadTilt.value.set(gestureState.headTilt.x, gestureState.headTilt.y);
      uThumbExt.value = gestureState.thumbExt;
      uIndexExt.value = gestureState.indexExt;
      uMiddleExt.value = gestureState.middleExt;
      uRingExt.value = gestureState.ringExt;
      uPinkyExt.value = gestureState.pinkyExt;

      // Hand X position drives global Y rotation of the visualization
      if (state.P.gestureControl === 'Hand') {
        const targetRotY = gestureState.handPos.x * 0.15; // map hand X to radians
        uHandRotY.value = THREE.MathUtils.lerp(uHandRotY.value, targetRotY, 0.08);
      } else {
        uHandRotY.value = THREE.MathUtils.lerp(uHandRotY.value, 0, 0.08);
      }

      // Gesture visual sphere
      if (state.gestureVisualSphere) {
        if (state.P.gestureControl === 'Hand') {
          state.gestureVisualSphere.position.set(gestureState.handPos.x, 0.4, gestureState.handPos.z);
          state.gestureVisualSphere.visible = true;
          state.gestureVisualSphere.material.color.setRGB(1.0 - gestureState.handOpen, gestureState.handOpen, 1.0);
        } else {
          state.gestureVisualSphere.visible = false;
        }
      }

      // Camera parallax
      if (controls) {
        if (state.P.gestureControl === 'Head') {
          const targetX = gestureState.headPos.x * state.HALF * 0.65;
          const targetZ = gestureState.headPos.y * state.HALF * 0.65;
          const targetY = state.HALF * 0.4 + (gestureState.headPos.y * state.HALF * 0.25);
          controls.target.x = THREE.MathUtils.lerp(controls.target.x, targetX, 0.1);
          controls.target.z = THREE.MathUtils.lerp(controls.target.z, targetZ, 0.1);
          controls.target.y = THREE.MathUtils.lerp(controls.target.y, targetY, 0.1);
        } else {
          controls.target.x = THREE.MathUtils.lerp(controls.target.x, 0, 0.1);
          controls.target.z = THREE.MathUtils.lerp(controls.target.z, 0, 0.1);
          controls.target.y = THREE.MathUtils.lerp(controls.target.y, state.HALF * 0.4, 0.1);
        }
        controls.update();
      }

      // Age waves
      for (let i = state.waves.length - 1; i >= 0; i--) {
        state.waves[i].age += dt;
        if (state.waves[i].age * state.waves[i].speed > state.EXT * 1.25) state.waves.splice(i, 1);
      }

      // Write waves to uniforms
      if (state.waves.length > 0) {
        const n = Math.min(state.waves.length, MAXW);
        for (let i = 0; i < MAXW; i++) {
          if (i < n) {
            const w = state.waves[i];
            waveArrA[i].set(w.ox, w.oz, w.age, w.speed);
            waveArrB[i].set(w.color.r, w.color.g, w.color.b, 1);
          } else {
            waveArrA[i].set(0, 0, 0, 0);
            waveArrB[i].set(0, 0, 0, 0);
          }
        }
        uCount.value = n;
      } else {
        const a = t * 1.1;
        const age1 = (t + 1.2) % 2.8;
        const age2 = (t + 2.4) % 2.8;
        waveArrA[0].set(Math.sin(a) * state.HALF * 0.5, Math.cos(a) * state.HALF * 0.5, age1, state.P.waveSpeed);
        waveArrB[0].set(1.0, 0.15, 0.55, 1);
        waveArrA[1].set(Math.sin(a + Math.PI) * state.HALF * 0.6, Math.cos(a + Math.PI) * state.HALF * 0.6, age2, state.P.waveSpeed);
        waveArrB[1].set(0.06, 0.72, 1.0, 1);
        for (let i = 2; i < MAXW; i++) { waveArrA[i].set(0, 0, 0, 0); waveArrB[i].set(0, 0, 0, 0); }
        uCount.value = 2;
      }

      renderer.compute(fieldFn);
      pc.position.set(Math.sin(t * 0.5) * state.HALF * 0.3, state.HALF * 0.4, Math.cos(t * 0.5) * state.HALF * 0.3);
      renderer.render(scene, camera);

      // Snap
      checkSnap();

      // FPS
      frames++; fpsT += dt;
      if (fpsT >= 0.5) {
        const fpsEl = document.getElementById('fps');
        if (fpsEl) fpsEl.textContent = `${state.CELLS} cells · ${Math.round(frames / fpsT)} fps`;
        frames = 0; fpsT = 0;
      }
    }

    boot('initializing renderer');
    renderer.init().then(() => {
      boot('running');
      const bootEl = document.getElementById('boot');
      if (bootEl) bootEl.style.opacity = '0.35';
      syncUniforms(state);
      applyPreset(state, state.P.visualPreset);

      const loader = document.getElementById('loadingOverlay');
      if (loader) {
        loader.style.opacity = '0';
        setTimeout(() => loader!.style.display = 'none', 600);
      }

      renderer.setAnimationLoop(animate);
    }).catch(err => showError('Renderer init failed', (err && err.message) + '\n\n' + (err && err.stack)));

  } catch (err) {
    showError('Setup failed', (err && (err as Error).message) + '\n\n' + ((err as any)?.stack || ''));
  }
}

// Helper: radius to shape surface
function radiusFor(dir: any, planes: any, count: any, isSphere: any, sphereR: any) {
  const r = float(999).toVar();
  If(isSphere.greaterThan(0.5), () => { r.assign(sphereR); }).Else(() => {
    Loop({ start: uint(0), end: uint(MAXF), type: 'uint', condition: '<' }, ({ i }) => {
      If(float(i).lessThan(count), () => {
        const pl = planes.element(i);
        const dp = dot(dir, pl.xyz);
        If(dp.greaterThan(float(1e-4)), () => { r.assign(min(r, pl.w.div(dp))); });
      });
    });
  });
  return r;
}

function buildPanel(state: RendererState) {
  const body = document.getElementById('panelBody');
  if (!body) return;

  const spec: any[] = [
    ['Wave Physics'],
    ['velocity', 'waveSpeed', 8, 45, 1], ['decay', 'waveDecay', 0.2, 3, 0.1],
    ['width', 'waveWidth', 2, 20, 0.5], ['amplitude', 'waveAmp', 0.5, 3.5, 0.1],
    ['spike height', 'heightMul', 0.5, 7, 0.1], ['swirl', 'spin', 0, 1.2, 0.05],
    ['Sustain Rate'],
    ['pulse speed', 'sustainPulseRate', 0.15, 0.85, 0.05],
    ['Synth'],
    ['attack', 'attack', 0.01, 0.5, 0.01], ['release', 'release', 0.1, 3.5, 0.1],
  ];

  for (const s of spec) {
    if (s.length === 1) {
      const g = document.createElement('div'); g.className = 'grp'; g.textContent = s[0]; body.appendChild(g); continue;
    }
    const [label, key, min, max, step] = s;
    const row = document.createElement('div'); row.className = 'row';
    const lab = document.createElement('label'); lab.textContent = label;
    const inp = document.createElement('input'); inp.type = 'range'; inp.min = min; inp.max = max; inp.step = step; inp.value = (state.P as any)[key];
    const val = document.createElement('span'); val.className = 'val'; val.textContent = (+(state.P as any)[key]).toString();
    inp.oninput = () => { (state.P as any)[key] = parseFloat(inp.value); val.textContent = (state.P as any)[key]; syncUniforms(state); };
    row.append(lab, inp, val); body.appendChild(row);
  }

  // Selects
  const mk = (label: string, opts: [string, string][], val: string, on: (v: string) => void) => {
    const g = document.createElement('div'); g.className = 'grp'; g.textContent = label; body.appendChild(g);
    const row = document.createElement('div'); row.className = 'row';
    const sel = document.createElement('select');
    opts.forEach(([v, t]) => { const o = document.createElement('option'); o.value = v; o.textContent = t; if (v === val) o.selected = true; sel.appendChild(o); });
    sel.onchange = () => on(sel.value);
    row.appendChild(sel); body.appendChild(row);
  };

  mk('Gesture Input', [['Off', 'Disabled'], ['Hand', 'Hand Magic (Open Palm)'], ['Head', 'Head Parallax']], 'Off', async (v) => {
    state.P.gestureControl = v as any;
    if (v !== 'Off') {
      const initializedWebcam = await startWebcam();
      if (initializedWebcam) await loadMediaPipe();
    } else {
      stopWebcam();
      if (state.gestureVisualSphere) state.gestureVisualSphere.visible = false;
      state.scene.rotation.set(0, 0, 0);
      resetGestureState();
    }
  });

  mk('Visual Preset', Object.keys(PRESETS).map(k => [k, k]), 'Void', v => applyPreset(state, v));
  mk('Blend Mode', [['0', 'Harmonic Ratios'], ['1', 'Additive Glow']], '0', v => { state.P.blendMode = parseInt(v); syncUniforms(state); });
  mk('Geometry', [['Dynamic', 'Dynamic (by interval)'], ['2', 'Pyramid'], ['3', 'Pentahedron'], ['4', 'Cube'], ['5', 'Dodecahedron'], ['6', 'Hex Prism'], ['7', 'Heptagram'], ['sphere', 'Sphere']], 'Dynamic', v => {
    state.P.shapeMode = v;
    if (v === 'Dynamic') detect(state); else setShape(state, v);
  });
  mk('Waveform', [['sine', 'Sine'], ['triangle', 'Triangle'], ['sawtooth', 'Sawtooth'], ['square', 'Square']], 'triangle', v => state.P.synthWave = v as OscillatorType);

  // Grid size
  const g = document.createElement('div'); g.className = 'grp'; g.textContent = 'Grid (reload)'; body.appendChild(g);
  const row = document.createElement('div'); row.className = 'row';
  const sel = document.createElement('select');
  [24, 28, 34, 40, 46].forEach(c => {
    const o = document.createElement('option'); o.value = String(c); o.textContent = c + '×' + c;
    if (c === state.G) o.selected = true; sel.appendChild(o);
  });
  sel.onchange = () => { urlp.set('grid', sel.value); location.search = urlp.toString(); };
  row.appendChild(sel); body.appendChild(row);

  const panelH3 = document.querySelector('#panel h3');
  if (panelH3) {
    panelH3.onclick = () => {
      const p = document.getElementById('panel');
      p?.classList.toggle('collapsed');
      const caret = document.getElementById('caret');
      if (caret) caret.textContent = p?.classList.contains('collapsed') ? '▸' : '▾';
    };
  }
}

const urlp = new URLSearchParams(location.search);

function setupPWAInstall() {
  let deferredPrompt: any;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    const btn = document.getElementById('btnInstall');
    if (btn) (btn as HTMLElement).style.display = 'flex';
  });

  document.getElementById('btnInstall')?.addEventListener('click', async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        const btn = document.getElementById('btnInstall');
        if (btn) (btn as HTMLElement).style.display = 'none';
      }
      deferredPrompt = null;
    }
  });
}